'use client';
import { useBlogPage } from '@/hooks/useBlogPage';
import { useBlogTheme } from '@/hooks/useBlogTheme';
import { cn } from '@/lib/utils';
import { getNudgeCard } from '@/utils/blog/new/getNudgeCard';
import Link from 'next/link';
import { useState, useEffect } from 'react';

export const NudgeCard = ({ page }) => {
  const [data, setData] = useState(null);
  const { isBlogLightTheme } = useBlogTheme();
  const { isSingleBlogPage } = useBlogPage();

  useEffect(() => {
    if (!page) return;

    const fetchData = async () => {
      const result = await getNudgeCard({ page });
      setData(result);
    };

    fetchData();
  }, [page]);

  if (!data) return null;

  return (
    <>
      <section className={` ${isBlogLightTheme ? 'bg-white' : ''}`}>
        <div
          className={`mx-auto pt-40 ${
            isSingleBlogPage && 'px-[30px] md:px-[70px] pb-[100px] md:pb-[86px]'
          }`}
        >
          {/* Gradient border */}
          <div
            className={`${
              isBlogLightTheme
                ? 'bg-[#D4D4D4]'
                : 'bg-[radial-gradient(100%_100%_at_50.17%_0%,rgba(255,255,255,0.88)_0%,#404040_100%)]'
            } p-[1px] rounded-3xl mx-auto w-full max-w-screen-xl`}
          >
            {/* Card content */}
            <div
              className={`relative  px-4 pt-[93px] pb-[66px] rounded-3xl  text-center ${
                isBlogLightTheme
                  ? 'text-black bg-white'
                  : ' text-white bg-[#020C0D]'
              }`}
            >
              {/* Gradient overlay  */}
              <Gradient lightTheme={isBlogLightTheme ? true : false} />

              {/* heading */}
              <h3 className="z-50 relative pb-[17px] text-2xl md:text-5xl">
                <span
                  className={cn(
                    data.heading[0].style === 'fancy'
                      ? 'italic font-instrument-serif'
                      : 'font-inter-tight'
                  )}
                >
                  {data.heading[0].text}{' '}
                </span>
                <span
                  className={cn(
                    data.heading[1].style === 'fancy'
                      ? 'italic font-instrument-serif'
                      : 'font-inter-tight'
                  )}
                >
                  {data.heading[1].text}
                </span>
              </h3>

              {/* description */}
              <p
                className={`z-50 relative mx-auto mb-[18px] max-w-[90%] lg:max-w-[60%] xl:max-w-[40%] font-lexend font-light max-md:text-sm ${
                  isBlogLightTheme ? 'text-[#6D6D6D]' : 'text-[#C5C5C5] '
                }`}
              >
                <span dangerouslySetInnerHTML={{ __html: data.description }} />
              </p>

              {/* Buttons */}
              <div className="z-50 relative flex md:flex-row flex-col justify-center items-center gap-[12px] md:gap-[19px]">
                {/* First button */}
                <div
                  className={cn(
                    'bg-[radial-gradient(143.68%_1448.31%_at_-17.71%_-36%,rgba(255,255,255,0.88)_0%,#404040_100%)] p-[1px] rounded-full w-[175px] h-[47px]'
                  )}
                >
                  <Link
                    href={data.btns[0].to}
                    className="flex justify-center items-center opacity-100 hover:opacity-90 rounded-full w-[173px] h-[45px] font-inter-tight transition-opacity duration-200"
                    style={{
                      background: data.btns[0].bg_color || '#071112',
                      color: data.btns[0].text_color || '#85BCC0',
                    }}
                  >
                    {data.btns[0].label}
                  </Link>
                </div>

                {/* Second button */}
                <Link
                  href={data.btns[1].to}
                  className="flex justify-center items-center opacity-100 hover:opacity-90 border rounded-full w-[175px] h-[47px] font-inter-tight transition-opacity duration-200"
                  style={{
                    background: data.btns[1].bg_color || '#FFFFFF',
                    color: data.btns[1].text_color || '#085157',
                    border: `1px solid ${data.btns[1].bg_color}`,
                  }}
                >
                  {data.btns[1].label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export const Gradient = ({ lightTheme }) => {
  return (
    <div className="absolute inset-0 rounded-3xl w-full h-full overflow-hidden">
      {lightTheme ? (
        <div
          className={`bottom-0 left-1/2 z-10 absolute bg-[#56BCC5] blur-[64px] rounded-[50%] w-[100%] h-[45%] -translate-x-1/2 translate-y-[30%]`}
        ></div>
      ) : (
        <>
          <div className="bottom-0 left-1/2 z-30 absolute bg-[#56BCC5] blur-[34px] rounded-[50%] w-3/5 h-[30%] -translate-x-1/2 translate-y-[65%]"></div>

          <div className="bottom-0 left-1/2 z-20 absolute bg-[linear-gradient(132.15deg,#00535B_-7.9%,#00646D_90.3%)] blur-[64px] rounded-[50%] w-[85%] h-[53%] -translate-x-1/2 translate-y-[50%]"></div>

          <div
            className={`bottom-0 left-1/2 z-10 absolute bg-[#042629] blur-[64px] rounded-[50%] w-[150%] h-[85%] -translate-x-1/2 translate-y-[30%]`}
          ></div>
        </>
      )}
    </div>
  );
};
